import { useState, useRef, useEffect, useCallback } from 'react';
import { useStore } from '../store';
import { Btn, Input, Select, Card, Modal, Confirm, Textarea, useT, THEME, pastelBtn } from './UI';
import { api } from '../api';
import { LOGO_BASE64 } from '../utils/logo';
import EntrenamientoIA from './EntrenamientoIA';
import { EspaciosTaller } from './Misc';

/* ══ Mini editor WYSIWYG para cabecera/pie ══ */
function WysiwygEditor({ label, value, onChange, placeholder }) {
  const theme = useT();
  const ref = useRef(null);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, []); // eslint-disable-line

  const exec = (cmd, val) => {
    ref.current.focus();
    document.execCommand(cmd, false, val);
    onChange(ref.current.innerHTML);
  };

  const toolBtns = [
    { cmd:'bold',          icon:'𝐁',      title:'Negrita' },
    { cmd:'italic',        icon:'𝘐',      title:'Cursiva' },
    { cmd:'underline',     icon:'U̲',      title:'Subrayado' },
    { cmd:'justifyLeft',   icon:'⬤·',     title:'Izquierda' },
    { cmd:'justifyCenter', icon:'·⬤·',    title:'Centrar' },
    { cmd:'justifyRight',  icon:'·⬤',     title:'Derecha' },
    { cmd:'insertHTML',    icon:'—',       title:'Línea separadora', val:'<hr style="border:1px dashed #000;margin:2px 0"/>' },
  ];

  const fontSizes = ['10px','11px','12px','13px','14px','16px','18px'];

  return (
    <div style={{ marginBottom:4 }}>
      {label && <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:4 }}>{label}</label>}

      {/* Toolbar */}
      <div style={{ display:'flex', gap:4, flexWrap:'wrap', padding:'6px 8px', background:theme.surface2, border:`1px solid ${theme.borderStrong}`, borderBottom:'none', borderRadius:'8px 8px 0 0', alignItems:'center' }}>
        {toolBtns.map(b => (
          <button key={b.cmd} title={b.title}
            onMouseDown={e => { e.preventDefault(); exec(b.cmd, b.val); }}
            style={{ background:'none', border:`1px solid ${theme.borderStrong}`, borderRadius:4, padding:'2px 7px', fontSize:12, cursor:'pointer', fontWeight:600, color:theme.body }}>
            {b.icon}
          </button>
        ))}
        <select title="Tamaño fuente" onChange={e => exec('fontSize', e.target.value)} defaultValue=""
          style={{ padding:'2px 4px', border:`1px solid ${theme.borderStrong}`, borderRadius:4, fontSize:11, outline:'none' }}>
          <option value="" disabled>Tamaño</option>
          {fontSizes.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button title="Limpiar formato" onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }}
          style={{ background:'none', border:`1px solid ${theme.borderStrong}`, borderRadius:4, padding:'2px 7px', fontSize:11, cursor:'pointer', color:theme.faint }}>
          ✕ fmt
        </button>
        <button title="Borrar todo" onMouseDown={e => { e.preventDefault(); ref.current.innerHTML=''; onChange(''); }}
          style={{ background:'none', border:`1px solid ${theme.borderStrong}`, borderRadius:4, padding:'2px 7px', fontSize:11, cursor:'pointer', color:'#ef4444' }}>
          🗑️
        </button>
      </div>

      {/* Editable area */}
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={() => onChange(ref.current.innerHTML)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        data-placeholder={placeholder}
        style={{
          minHeight:70,
          padding:'8px 10px',
          border:`1px solid ${focused ? theme.primary : theme.borderStrong}`,
          borderRadius:'0 0 8px 8px',
          fontSize:13,
          outline:'none',
          fontFamily:'Courier New, monospace',
          lineHeight:1.6,
          color:theme.ink,
          background:theme.surface,
          position:'relative',
        }}
      />
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          display: block;
        }
      `}</style>

      {/* Vista previa */}
      {value && (
        <div style={{ marginTop:6, padding:'6px 10px', ...pastelBtn(theme,'amber'), borderRadius:6, fontSize:11 }}>
          <span style={{ color: theme===THEME.dark ? '#fbbf24' : '#92400e', fontWeight:600, fontSize:10 }}>VISTA PREVIA (impresión):</span>
          <div style={{ fontFamily:'Courier New, monospace', fontSize:12, marginTop:3, color: theme===THEME.dark ? theme.ink : '#000' }}
            dangerouslySetInnerHTML={{ __html: value }} />
        </div>
      )}
    </div>
  );
}

export default function Configuracion() {
  const theme = useT();
  const { data, updateConfig, auth } = useStore();
  const [cfg, setCfg] = useState({ ...data.config });
  const [saved, setSaved] = useState(false);
  const [restoreMsg, setRestoreMsg] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [tab, setTab] = useState('empresa');
  const [waStatus, setWaStatus] = useState(null);
  const [waQrTs, setWaQrTs] = useState(0);
  const [waReconectando, setWaReconectando] = useState(false);
  const [users, setUsers] = useState([]);
  const [userModal, setUserModal] = useState(null); // null | 'nuevo' | user obj
  const [userForm, setUserForm] = useState({ username:'', password:'', nombre:'', rol:'vendedor' });
  const [userMsg, setUserMsg] = useState('');
  const [confirmDel, setConfirmDel] = useState(null);
  const [portalStats, setPortalStats] = useState(null);
  const [portalSort, setPortalSort] = useState({ campo:'ultima', dir:'desc' });
  const [ignorados, setIgnorados] = useState(null);
  const [whitelist, setWhitelist] = useState(null);
  const [nuevoEmail, setNuevoEmail] = useState('');
  const [ollamaModelos, setOllamaModelos] = useState(null);
  const [ollamaError, setOllamaError] = useState('');

  const isAdmin = auth?.rol === 'admin';

  const fetchUsers = async () => {
    try {
      const r = await fetch('/api/users', { headers:{ Authorization: 'Bearer ' + localStorage.getItem('ri_token') } });
      if (r.ok) setUsers(await r.json());
    } catch {}
  };

  const fetchPortalStats = useCallback(async () => {
    try {
      const r = await fetch('/api/portal-stats', { headers:{ Authorization: 'Bearer ' + localStorage.getItem('ri_token') } });
      if (r.ok) setPortalStats(await r.json());
    } catch {}
  }, []);

  const fetchIgnorados = useCallback(async () => {
    try {
      const r = await fetch('/api/pop3/ignorados', { headers:{ Authorization: 'Bearer ' + localStorage.getItem('ri_token') } });
      if (r.ok) setIgnorados(await r.json());
    } catch {}
  }, []);

  const fetchWhitelist = useCallback(async () => {
    try {
      const r = await fetch('/api/pop3/whitelist', { headers:{ Authorization: 'Bearer ' + localStorage.getItem('ri_token') } });
      if (r.ok) setWhitelist(await r.json());
    } catch {}
  }, []);

  const addWhitelist = useCallback(async (email) => {
    if (!email.trim()) return;
    await fetch('/api/pop3/whitelist', { method:'POST', headers:{ 'Content-Type':'application/json', Authorization: 'Bearer ' + localStorage.getItem('ri_token') }, body: JSON.stringify({ email }) });
    setWhitelist(prev => [...(prev||[]), email.toLowerCase().trim()].sort());
    setNuevoEmail('');
  }, []);

  const removeWhitelist = useCallback(async (email) => {
    await fetch('/api/pop3/whitelist/' + encodeURIComponent(email), { method:'DELETE', headers:{ Authorization: 'Bearer ' + localStorage.getItem('ri_token') } });
    setWhitelist(prev => prev.filter(e => e !== email));
  }, []);

  const desIgnorar = useCallback(async (email) => {
    await fetch('/api/pop3/ignorados/' + encodeURIComponent(email), { method:'DELETE', headers:{ Authorization: 'Bearer ' + localStorage.getItem('ri_token') } });
    setIgnorados(prev => prev.filter(e => e !== email));
  }, []);

  const fetchOllamaModelos = useCallback(async (url) => {
    setOllamaError('');
    setOllamaModelos(null);
    try {
      const r = await fetch('/api/ollama-modelos?url=' + encodeURIComponent(url || 'http://192.168.0.202:11434'),
        { headers:{ Authorization: 'Bearer ' + localStorage.getItem('ri_token') } });
      if (!r.ok) throw new Error('respuesta no ok');
      const d = await r.json();
      setOllamaModelos(d.models || []);
    } catch {
      setOllamaModelos([]);
      setOllamaError('No se pudo conectar con Ollama. Comprueba la URL o que el servicio esté arriba.');
    }
  }, []);

  useEffect(() => { if (isAdmin && tab === 'usuarios') fetchUsers(); }, [tab, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAdmin && tab === 'portal') fetchPortalStats(); }, [tab, isAdmin, fetchPortalStats]);
  useEffect(() => { if (isAdmin && tab === 'email') { fetchWhitelist(); fetchIgnorados(); } }, [tab, isAdmin, fetchWhitelist, fetchIgnorados]);
  useEffect(() => { if (isAdmin && tab === 'paco') fetchOllamaModelos(cfg.ollamaUrl); }, [tab, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchWaStatus = useCallback(async () => {
    try {
      const r = await fetch('http://192.168.0.24:3010/status');
      const d = await r.json();
      setWaStatus(d);
    } catch {
      setWaStatus({ ok:false, status:'sin respuesta' });
    }
  }, []);

  useEffect(() => {
    if (!isAdmin || tab !== 'whatsapp') return;
    fetchWaStatus();
    const id = setInterval(fetchWaStatus, 4000);
    return () => clearInterval(id);
  }, [tab, isAdmin, fetchWaStatus]);

  async function reconectarWa() {
    setWaReconectando(true);
    try {
      await fetch('http://192.168.0.24:3010/reconectar', { method:'POST' });
      setWaQrTs(Date.now());
      setTimeout(fetchWaStatus, 1500);
    } catch {}
    setWaReconectando(false);
  }

  async function saveUser() {
    if (!userForm.username || !userForm.nombre || !userForm.rol) return setUserMsg('Rellena todos los campos.');
    if (userModal === 'nuevo' && !userForm.password) return setUserMsg('La contraseña es obligatoria.');
    try {
      if (userModal === 'nuevo') {
        await fetch('/api/users', { method:'POST', headers:{ 'Content-Type':'application/json', Authorization:'Bearer '+localStorage.getItem('ri_token') }, body: JSON.stringify(userForm) });
      } else {
        const body = { nombre: userForm.nombre, rol: userForm.rol };
        if (userForm.password) body.password = userForm.password;
        await fetch('/api/users/'+userModal.id, { method:'PUT', headers:{ 'Content-Type':'application/json', Authorization:'Bearer '+localStorage.getItem('ri_token') }, body: JSON.stringify(body) });
      }
      setUserModal(null); setUserMsg(''); setUserForm({ username:'', password:'', nombre:'', rol:'vendedor' });
      fetchUsers();
    } catch(e) { setUserMsg('Error al guardar.'); }
  }

  async function deleteUser(id) {
    await fetch('/api/users/'+id, { method:'DELETE', headers:{ Authorization:'Bearer '+localStorage.getItem('ri_token') } });
    setConfirmDel(null); fetchUsers();
  }

  const upd = (f, v) => setCfg(c => ({ ...c, [f]: v }));

  function save() {
    updateConfig(cfg);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  async function handleRestore(e) {
    const file = e.target.files[0];
    if (!file) return;
    setRestoring(true);
    setRestoreMsg('');
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await api.restore(data);
      setRestoreMsg('✓ Backup restaurado. Recarga la página para ver los datos.');
    } catch(err) {
      setRestoreMsg('❌ Error: ' + (err.message || 'archivo inválido'));
    }
    setRestoring(false);
    e.target.value = '';
  }

  const tabs = [
    { id:'empresa',   label:'🏪 Empresa' },
    { id:'tickets',   label:'🧾 Tickets / Térmica' },
    { id:'facturas',  label:'📄 Facturas A4' },
    { id:'numeracion',label:'🔢 Numeración' },
    { id:'espacios',  label:'🗂️ Espacios taller' },
    ...(isAdmin ? [{ id:'usuarios', label:'👥 Usuarios' }] : []),
    ...(isAdmin ? [{ id:'portal',   label:'📊 Portal web' }] : []),
    ...(isAdmin ? [{ id:'whatsapp',  label:'📱 WhatsApp' }] : []),
    ...(isAdmin ? [{ id:'paco',      label:'🤖 PACO (IA)' }] : []),
    ...(isAdmin ? [{ id:'email',     label:'📧 Email / POP3' }] : []),
    ...(isAdmin ? [{ id:'verifactu', label:'📋 VERI*FACTU' }] : []),
  ];

  return (
    <div style={{ maxWidth:740 }}>
      {/* Tabs */}
      <div style={{ display:'flex', gap:0, borderBottom:`1px solid ${theme.border}`, marginBottom:18 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding:'8px 16px', fontSize:13, fontWeight: tab===t.id ? 700 : 400,
            color: tab===t.id ? '#1a56db' : '#6b7280',
            background:'none', border:'none', borderBottom: tab===t.id ? '2px solid #1a56db' : '2px solid transparent',
            cursor:'pointer', transition:'all .15s',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── EMPRESA ── */}
      {tab === 'empresa' && (
        <Card style={{ marginBottom:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div style={{ gridColumn:'1/-1' }}><Input label="Nombre comercial" value={cfg.nombre} onChange={v=>upd('nombre',v)} /></div>
            <Input label="CIF / NIF"  value={cfg.cif}      onChange={v=>upd('cif',v)} />
            <Input label="Teléfono"   value={cfg.telefono}  onChange={v=>upd('telefono',v)} />
            <Input label="WhatsApp — prefijo (ej: 34)" value={cfg.whatsappPrefix||''} onChange={v=>upd('whatsappPrefix',v)} placeholder="34" />
            <Input label="WhatsApp — número sin prefijo" value={cfg.whatsappNum||''} onChange={v=>upd('whatsappNum',v)} placeholder="629013740" />
            <div style={{ gridColumn:'1/-1' }}><Input label="Dirección" value={cfg.direccion} onChange={v=>upd('direccion',v)} /></div>
            <Input label="Email"      value={cfg.email}     onChange={v=>upd('email',v)} type="email" />
            <Input label="Web"        value={cfg.web}       onChange={v=>upd('web',v)} />
            <Select label="IVA por defecto (%)" value={String(cfg.iva)} onChange={v=>upd('iva',+v)}
              options={['0','4','10','21'].map(v=>({value:v,label:`${v}%`}))} />
            <div style={{ gridColumn:'1/-1' }}>
              <Textarea label="Horario de la tienda" value={cfg.horario||''} onChange={v=>upd('horario',v)} rows={3}
                placeholder={'L-V: 9:30 - 14:00 y 16:30 - 19:00\nSábados: 9:30 - 13:00'} />
            </div>
          </div>
        </Card>
      )}

      {/* ── TICKETS / TÉRMICA ── */}
      {tab === 'tickets' && (
        <>
          <Card style={{ marginBottom:14 }} title="Logo en tickets térmicos">
            <div style={{ display:'flex', gap:16, alignItems:'center', marginBottom:12 }}>
              <img src={LOGO_BASE64} alt="logo" style={{ height:50, border:`1px solid ${theme.border}`, borderRadius:6, padding:4 }} />
              <div>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>Logo cargado: root_informatica</div>
                <div style={{ fontSize:12, color:theme.muted, marginBottom:8 }}>Se imprime en blanco y negro (adecuado para impresoras térmicas)</div>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13 }}>
                  <input type="checkbox" checked={cfg.ticketUsarLogo !== false}
                    onChange={e => upd('ticketUsarLogo', e.target.checked)} style={{ width:16, height:16 }} />
                  Mostrar logo en tickets y resguardos
                </label>
              </div>
            </div>
            <div style={{ fontSize:12, color:theme.muted, background:'#f0f9ff', borderRadius:6, padding:'8px 10px' }}>
              💡 Si el logo sale muy grande o pequeño, se puede ajustar en los ajustes de la impresora al imprimir.
            </div>
          </Card>

          <Card style={{ marginBottom:14 }} title="🕐 Ajuste de fecha/hora">
            <div style={{ fontSize:13, color:theme.body, marginBottom:10 }}>
              Los tickets usan la fecha del sistema. Si la fecha de tu PC no es correcta, corrígela aquí para que aparezca en los documentos.
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <Input label="Fecha manual (dejar vacío = fecha del sistema)" type="date"
                value={cfg.fechaManual || ''} onChange={v=>upd('fechaManual',v)} />
              <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:2 }}>
                <Btn variant="ghost" size="sm" onClick={() => upd('fechaManual','')}>↺ Usar fecha del sistema</Btn>
              </div>
            </div>
            {cfg.fechaManual && (
              <div style={{ marginTop:8, background:'#fef3c7', border:'1px solid #fbbf24', borderRadius:6, padding:'6px 10px', fontSize:12, color:'#92400e' }}>
                ⚠️ Fecha manual activa: <b>{new Date(cfg.fechaManual).toLocaleDateString('es-ES')}</b> — los tickets usarán esta fecha
              </div>
            )}
          </Card>

          <Card style={{ marginBottom:14 }} title="✏️ Cabecera personalizada (reemplaza dirección/teléfono)">
            <div style={{ fontSize:12, color:theme.muted, marginBottom:10 }}>
              Si dejas esto vacío se imprime la dirección y teléfono de la empresa. Si lo rellenas, se imprime este texto debajo del logo.
            </div>
            <WysiwygEditor
              value={cfg.ticketCabecera || ''}
              onChange={v => upd('ticketCabecera', v)}
              placeholder="Ej: C/ Informática 12 · 48001 Bilbao&#10;Tel: 944 123 456 · rootinformatica.es"
            />
          </Card>

          <Card style={{ marginBottom:14 }} title="✏️ Pie personalizado (reemplaza texto final)">
            <div style={{ fontSize:12, color:theme.muted, marginBottom:10 }}>
              Si dejas esto vacío se imprime el texto estándar de agradecimiento / garantía. Si lo rellenas, se imprime este texto al final del ticket.
            </div>
            <WysiwygEditor
              value={cfg.ticketPie || ''}
              onChange={v => upd('ticketPie', v)}
              placeholder="Ej: Gracias por su confianza&#10;Garantía 3 meses · rootinformatica.es"
            />
          </Card>
        </>
      )}

      {/* ── FACTURAS A4 ── */}
      {tab === 'facturas' && (
        <Card style={{ marginBottom:14 }} title="Logo en facturas A4">
          <div style={{ display:'flex', gap:16, alignItems:'center', marginBottom:12 }}>
            <img src={LOGO_BASE64} alt="logo" style={{ height:60, border:`1px solid ${theme.border}`, borderRadius:6, padding:4,  }} />
            <div>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>Logo de Root Informática</div>
              <div style={{ fontSize:12, color:theme.muted, marginBottom:8 }}>Aparece en la cabecera de facturas, proformas y presupuestos</div>
              <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13 }}>
                <input type="checkbox" checked={cfg.facturaUsarLogo !== false}
                  onChange={e => upd('facturaUsarLogo', e.target.checked)} style={{ width:16, height:16 }} />
                Mostrar logo en facturas A4
              </label>
            </div>
          </div>
          <div style={{ background:'#f0f9ff', borderRadius:8, padding:10, fontSize:12, color:'#0369a1' }}>
            💡 Para cambiar el logo, contacta con el desarrollador o sube un nuevo archivo <code>logo.js</code> en <code>src/utils/</code>
          </div>
        </Card>
      )}

      {/* ── NUMERACIÓN ── */}
      {tab === 'numeracion' && (
        <Card style={{ marginBottom:14 }}>
          <div style={{ fontSize:12, color:theme.muted, marginBottom:10 }}>Siguiente número que se usará al crear cada documento.</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
            <Input label="Siguiente ticket"         value={cfg.sigTicket}          onChange={v=>upd('sigTicket',+v)}          type="number" />
            <Input label="Siguiente factura"        value={cfg.sigFactura}         onChange={v=>upd('sigFactura',+v)}         type="number" />
            <Input label="Siguiente proforma"       value={cfg.sigProforma}        onChange={v=>upd('sigProforma',+v)}        type="number" />
            <Input label="Siguiente presupuesto"    value={cfg.sigPresupuesto}     onChange={v=>upd('sigPresupuesto',+v)}     type="number" />
            <Input label="Siguiente pedido prov."   value={cfg.sigPedido}          onChange={v=>upd('sigPedido',+v)}          type="number" />
            <Input label="Siguiente pedido cliente" value={cfg.sigPedidoCliente||1} onChange={v=>upd('sigPedidoCliente',+v)} type="number" />
            <Input label="Siguiente reparación"     value={cfg.sigReparacion}      onChange={v=>upd('sigReparacion',+v)}      type="number" />
          </div>
        </Card>
      )}

      {tab === 'espacios' && (
        <div>
          <div style={{ fontSize:12, color:theme.muted, marginBottom:14 }}>
            Boxes, estanterías u otros sitios físicos del taller. Verde = libre, rojo = ocupado por una reparación activa (se libera solo al entregar), gris = fuera de servicio (clic para alternar).
          </div>
          <EspaciosTaller />
        </div>
      )}

      {/* ── USUARIOS ── */}
      {tab === 'usuarios' && isAdmin && (
        <Card style={{ marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div style={{ fontSize:13, fontWeight:600, color:theme.body }}>Gestión de usuarios</div>
            <Btn variant="primary" onClick={() => { setUserForm({ username:'', password:'', nombre:'', rol:'vendedor' }); setUserMsg(''); setUserModal('nuevo'); }}>+ Nuevo usuario</Btn>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:theme.surface2 }}>
                {['Usuario','Nombre','Rol',''].map(h => (
                  <th key={h} style={{ padding:'8px 12px', textAlign:'left', color:theme.muted, fontSize:11, fontWeight:600, borderBottom:`1px solid ${theme.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} style={{ borderBottom:`1px solid ${theme.border}`, background: i%2 ? theme.surface2 : 'transparent' }}>
                  <td style={{ padding:'8px 12px', fontWeight:600 }}>{u.username}</td>
                  <td style={{ padding:'8px 12px' }}>{u.nombre}</td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600,
                      background: u.rol==='admin'?'#dbeafe':u.rol==='tecnico'?'#d1fae5':'#fef3c7',
                      color: u.rol==='admin'?'#1e40af':u.rol==='tecnico'?'#065f46':'#92400e' }}>
                      {u.rol}
                    </span>
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <div style={{ display:'flex', gap:4 }}>
                      <Btn size="sm" variant="ghost" onClick={() => { setUserForm({ username:u.username, password:'', nombre:u.nombre, rol:u.rol }); setUserMsg(''); setUserModal(u); }}>✏️</Btn>
                      {u.id !== auth.id && <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} onClick={() => setConfirmDel(u)}>🗑️</Btn>}
                    </div>
                  </td>
                </tr>
              ))}
              {!users.length && <tr><td colSpan={4} style={{ padding:20, textAlign:'center', color:theme.faint }}>Sin usuarios</td></tr>}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modal nuevo/editar usuario */}
      {userModal && (
        <Modal title={userModal === 'nuevo' ? 'Nuevo usuario' : 'Editar usuario'} onClose={() => { setUserModal(null); setUserMsg(''); }} width={420}>
          <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:14 }}>
            {userModal === 'nuevo' && (
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase' }}>Usuario</label>
                <input value={userForm.username} onChange={e=>setUserForm(f=>({...f,username:e.target.value}))}
                  style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} placeholder="nombre_usuario" />
              </div>
            )}
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase' }}>Nombre</label>
              <input value={userForm.nombre} onChange={e=>setUserForm(f=>({...f,nombre:e.target.value}))}
                style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} placeholder="Nombre completo" />
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase' }}>Contraseña {userModal !== 'nuevo' && <span style={{ color:theme.faint, fontWeight:400 }}>(dejar vacío = no cambiar)</span>}</label>
              <input type="password" value={userForm.password} onChange={e=>setUserForm(f=>({...f,password:e.target.value}))}
                style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }} placeholder="••••••••" />
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase' }}>Rol</label>
              <select value={userForm.rol} onChange={e=>setUserForm(f=>({...f,rol:e.target.value}))}
                style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }}>
                <option value="admin">Admin — acceso completo</option>
                <option value="vendedor">Vendedor — ventas y gestión</option>
                <option value="tecnico">Técnico — solo reparaciones</option>
              </select>
            </div>
          </div>
          {userMsg && <div style={{ background:'#fee2e2', color:'#991b1b', borderRadius:8, padding:'8px 12px', fontSize:13, marginBottom:12 }}>{userMsg}</div>}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn variant="outline" onClick={() => { setUserModal(null); setUserMsg(''); }}>Cancelar</Btn>
            <Btn variant="primary" onClick={saveUser}>Guardar</Btn>
          </div>
        </Modal>
      )}

      {confirmDel && (
        <Confirm msg={`¿Eliminar el usuario "${confirmDel.username}"?`}
          onOk={() => deleteUser(confirmDel.id)} onCancel={() => setConfirmDel(null)} />
      )}

      {/* ── PORTAL WEB — estadísticas ── */}
      {tab === 'portal' && isAdmin && (
        <Card style={{ marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:theme.body }}>Consultas recibidas en el portal</div>
              <div style={{ fontSize:12, color:theme.faint, marginTop:2 }}>Reparaciones buscadas por los clientes desde el portal público</div>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <Btn variant="ghost" size="sm" onClick={fetchPortalStats}>↺ Actualizar</Btn>
              <Btn variant="ghost" size="sm" style={{ color:'#ef4444' }} onClick={async () => {
                if (!window.confirm('¿Borrar todas las estadísticas del portal?')) return;
                await fetch('/api/portal-stats', { method:'DELETE', headers:{ Authorization:'Bearer '+localStorage.getItem('ri_token') } });
                setPortalStats([]);
              }}>🗑️ Borrar todo</Btn>
            </div>
          </div>
          {portalStats === null ? (
            <div style={{ padding:20, textAlign:'center', color:theme.faint, fontSize:13 }}>Cargando...</div>
          ) : portalStats.length === 0 ? (
            <div style={{ padding:20, textAlign:'center', color:theme.faint, fontSize:13 }}>Todavía no hay consultas registradas.</div>
          ) : (
            <>
              <div style={{ marginBottom:10, display:'flex', gap:20 }}>
                <div style={{ ...pastelBtn(theme,'blue'), borderRadius:8, padding:'8px 16px', textAlign:'center' }}>
                  <div style={{ fontSize:22, fontWeight:800, color: theme===THEME.dark ? '#93c5fd' : '#1a56db' }}>{portalStats.length}</div>
                  <div style={{ fontSize:11, color:theme.muted }}>reparaciones consultadas</div>
                </div>
                <div style={{ ...pastelBtn(theme,'green'), borderRadius:8, padding:'8px 16px', textAlign:'center' }}>
                  <div style={{ fontSize:22, fontWeight:800, color: theme===THEME.dark ? '#6ee7b7' : '#0e9f6e' }}>{portalStats.reduce((s,r)=>s+r.veces,0)}</div>
                  <div style={{ fontSize:11, color:theme.muted }}>consultas totales</div>
                </div>
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:theme.surface2 }}>
                    {[
                      { label:'Nº reparación', campo:'num' },
                      { label:'Cliente', campo:'cliente' },
                      { label:'Equipo', campo:'nombre' },
                      { label:'Consultas', campo:'veces' },
                      { label:'Última consulta', campo:'ultima' },
                    ].map(({label, campo}) => (
                      <th key={campo} onClick={() => setPortalSort(s => ({
                          campo, dir: s.campo === campo && s.dir === 'asc' ? 'desc' : 'asc'
                        }))}
                        style={{ padding:'8px 12px', textAlign:'left', color:theme.muted, fontSize:11, fontWeight:600, borderBottom:`1px solid ${theme.border}`, cursor:'pointer', userSelect:'none' }}>
                        {label}{portalSort.campo === campo ? (portalSort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...portalStats].sort((a,b) => {
                    const { campo, dir } = portalSort;
                    let va = a[campo], vb = b[campo];
                    if (campo === 'ultima') { va = va ? new Date(va).getTime() : 0; vb = vb ? new Date(vb).getTime() : 0; }
                    else if (campo === 'veces') { va = va || 0; vb = vb || 0; }
                    else { va = (va || '').toString().toLowerCase(); vb = (vb || '').toString().toLowerCase(); }
                    if (va < vb) return dir === 'asc' ? -1 : 1;
                    if (va > vb) return dir === 'asc' ? 1 : -1;
                    return 0;
                  }).map((r, i) => (
                    <tr key={r.num} style={{ borderBottom:`1px solid ${theme.border}`, background: i%2 ? theme.surface2 : 'transparent' }}>
                      <td style={{ padding:'8px 12px', fontWeight:700, color:'#1a56db', fontFamily:'monospace' }}>{r.num}</td>
                      <td style={{ padding:'8px 12px', color:theme.body }}>{r.cliente || '—'}</td>
                      <td style={{ padding:'8px 12px', color:theme.body }}>{r.nombre || '—'}</td>
                      <td style={{ padding:'8px 12px' }}>
                        <span style={{ ...pastelBtn(theme,'blue'), borderRadius:20, padding:'2px 10px', fontWeight:700, fontSize:12 }}>{r.veces}</span>
                      </td>
                      <td style={{ padding:'8px 12px', color:theme.muted, fontSize:12 }}>
                        {r.ultima ? new Date(r.ultima).toLocaleString('es-ES',{day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Card>
      )}

      {/* ── WHATSAPP — sesión/QR ── */}
      {tab === 'whatsapp' && isAdmin && (
        <Card style={{ marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:theme.body }}>Sesión de WhatsApp</div>
              <div style={{ fontSize:12, color:theme.faint, marginTop:2 }}>
                Estado:{' '}
                {waStatus === null ? 'comprobando...' : waStatus.ok
                  ? <span style={{ color:pastelBtn(theme,'green').color, fontWeight:700 }}>✓ conectado</span>
                  : <span style={{ color:pastelBtn(theme,'red').color, fontWeight:700 }}>✗ {waStatus.status || 'desconectado'}</span>}
              </div>
            </div>
            <Btn variant="ghost" size="sm" onClick={fetchWaStatus}>↺ Comprobar</Btn>
          </div>
          {waStatus && !waStatus.ok && (
            <div style={{ marginBottom:14 }}>
              <Btn variant="primary" size="sm" onClick={reconectarWa} disabled={waReconectando}>
                {waReconectando ? 'Generando...' : '📱 Generar código QR'}
              </Btn>
              {waQrTs > 0 && (
                <div style={{ marginTop:10 }}>
                  <div style={{ fontSize:12, color:theme.faint, marginBottom:6 }}>Escanea con WhatsApp → Dispositivos vinculados:</div>
                  <img src={`http://192.168.0.24:3010/qr?t=${waQrTs}`} alt="QR WhatsApp"
                    style={{ width:220, height:220, border:`1px solid ${theme.border}`, borderRadius:8, background:'#fff', padding:8 }}
                    onError={e => { e.target.style.display='none'; }} />
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {tab === 'paco' && isAdmin && (
        <Card style={{ marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:theme.body }}>Modelo de PACO</div>
              <div style={{ fontSize:12, color:theme.faint, marginTop:2 }}>PACO es el asistente IA del portal/WhatsApp. Elige qué modelo de Ollama usa.</div>
            </div>
            <Btn variant="ghost" size="sm" onClick={() => fetchOllamaModelos(cfg.ollamaUrl)}>↺ Refrescar modelos</Btn>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom:14 }}>
            <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase' }}>URL de Ollama</label>
            <input value={cfg.ollamaUrl || ''} onChange={e => upd('ollamaUrl', e.target.value)}
              onBlur={() => fetchOllamaModelos(cfg.ollamaUrl)}
              placeholder="http://192.168.0.202:11434"
              style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13, outline:'none', fontFamily:'monospace' }} />
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom:14 }}>
            <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase' }}>Modelo</label>
            {ollamaModelos === null ? (
              <div style={{ padding:10, color:theme.faint, fontSize:13 }}>Cargando modelos...</div>
            ) : ollamaModelos.length === 0 ? (
              <div style={{ padding:10, color:'#ef4444', fontSize:13, background:'#fee2e2', borderRadius:8 }}>{ollamaError || 'Sin modelos disponibles.'}</div>
            ) : (
              <select value={cfg.iaModelo || 'qwen2.5:14b'} onChange={e => upd('iaModelo', e.target.value)}
                style={{ padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13, outline:'none' }}>
                {ollamaModelos.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
            <div style={{ fontSize:11, color:theme.faint, marginTop:2 }}>Modelos más grandes (14b) responden mejor pero más lento. Modelos más pequeños (7b) son más rápidos, ideales para WhatsApp en vivo.</div>
          </div>

          {saved && <div style={{ background:'#dcfce7', color:'#166534', borderRadius:8, padding:'8px 12px', fontSize:13, marginBottom:10 }}>✓ Guardado</div>}
          <Btn variant="primary" onClick={save}>Guardar</Btn>
        </Card>
      )}

      {tab === 'paco' && isAdmin && (
        <div style={{ marginTop:14 }}>
          <div style={{ fontSize:13, fontWeight:700, color:theme.body, marginBottom:8 }}>Entrenamiento de PACO</div>
          <EntrenamientoIA />
        </div>
      )}

      {/* ── EMAIL / POP3 ── */}
      {tab === 'email' && isAdmin && (
        <Card style={{ marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:theme.body }}>Remitentes autorizados (whitelist)</div>
              <div style={{ fontSize:12, color:theme.faint, marginTop:2 }}>Solo aparecerán emails de estos remitentes al importar facturas</div>
            </div>
            <Btn variant="ghost" size="sm" onClick={fetchWhitelist}>↺ Actualizar</Btn>
          </div>

          {/* Añadir nuevo */}
          <div style={{ display:'flex', gap:8, marginBottom:14 }}>
            <input value={nuevoEmail} onChange={e => setNuevoEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addWhitelist(nuevoEmail)}
              placeholder="email@proveedor.com"
              style={{ flex:1, padding:'7px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:8, fontSize:13, outline:'none', fontFamily:'monospace' }} />
            <Btn variant="primary" onClick={() => addWhitelist(nuevoEmail)}>+ Añadir</Btn>
          </div>

          {whitelist === null && <div style={{ padding:16, textAlign:'center', color:theme.faint, fontSize:13 }}>Cargando...</div>}
          {whitelist && whitelist.length === 0 && (
            <div style={{ padding:16, textAlign:'center', color:'#f59e0b', fontSize:13, background:'#fef3c7', borderRadius:8 }}>
              ⚠ Sin remitentes configurados — no aparecerá ningún email en el importador
            </div>
          )}
          {whitelist && whitelist.length > 0 && (
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:theme.surface2 }}>
                  <th style={{ padding:'8px 12px', textAlign:'left', color:theme.muted, fontSize:11, fontWeight:600, borderBottom:`1px solid ${theme.border}` }}>Email remitente</th>
                  <th style={{ padding:'8px 12px', borderBottom:`1px solid ${theme.border}`, width:80 }}></th>
                </tr>
              </thead>
              <tbody>
                {whitelist.map((email, i) => (
                  <tr key={email} style={{ borderBottom:`1px solid ${theme.border}`, background: i%2 ? theme.surface2 : 'transparent' }}>
                    <td style={{ padding:'8px 12px', color:theme.body, fontFamily:'monospace', fontSize:12 }}>✅ {email}</td>
                    <td style={{ padding:'8px 12px', textAlign:'right' }}>
                      <Btn size="sm" variant="ghost" style={{ color:'#ef4444' }} onClick={() => removeWhitelist(email)}>🗑️</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {tab === 'verifactu' && isAdmin && (
        <Card>
          <div style={{ fontSize:15, fontWeight:700, color:theme.ink, marginBottom:16 }}>VERI*FACTU — Configuración</div>

          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontWeight:600, fontSize:13 }}>
              <input
                type="checkbox"
                checked={!!cfg.verifactu_enabled}
                onChange={e => setCfg(c => ({ ...c, verifactu_enabled: e.target.checked }))}
                style={{ width:16, height:16 }}
              />
              Activar envío de facturas a AEAT
            </label>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:theme.muted, textTransform:'uppercase', letterSpacing:'.04em', display:'block', marginBottom:4 }}>Entorno AEAT</label>
              <select
                value={cfg.verifactu_env || 'PRE'}
                onChange={e => setCfg(c => ({ ...c, verifactu_env: e.target.value }))}
                style={{ width:'100%', padding:'8px 10px', border:`1px solid ${theme.borderStrong}`, borderRadius:6, fontSize:13 }}
              >
                <option value="PRE">PRE (pruebas)</option>
                <option value="PRO">PRO (producción)</option>
              </select>
            </div>
            <Input
              label="Contraseña del certificado"
              type="password"
              value={cfg.verifactu_cert_pass || ''}
              onChange={v => setCfg(c => ({ ...c, verifactu_cert_pass: v }))}
            />
          </div>

          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
            <label style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, border:`1px solid ${theme.borderStrong}`, background:theme.surface, fontSize:13, fontWeight:500, cursor:'pointer', color:theme.body }}>
              📎 Subir certificado (.p12 / .pfx)
              <input
                type="file"
                accept=".p12,.pfx"
                style={{ display:'none' }}
                onChange={async e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append('cert', file);
                  const token = localStorage.getItem('token');
                  try {
                    const r = await fetch('/api/verifactu/cert', {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` },
                      body: fd,
                    });
                    if (!r.ok) throw new Error();
                    setCfg(c => ({ ...c, verifactu_cert_path: 'cargado' }));
                    alert('Certificado subido correctamente');
                  } catch {
                    alert('Error subiendo el certificado');
                  }
                  e.target.value = '';
                }}
              />
            </label>
            <span style={{ fontSize:12, color: cfg.verifactu_cert_path ? '#16a34a' : '#6b7280', fontWeight:600 }}>
              {cfg.verifactu_cert_path ? '✓ Certificado cargado' : 'Sin certificado'}
            </span>
          </div>

          <div style={{ fontSize:11, color:theme.faint, padding:'10px 12px', background:theme.surface2, borderRadius:6, lineHeight:1.6, marginBottom:12 }}>
            <b>PRE</b> = entorno de pruebas AEAT. <b>PRO</b> = producción real.<br/>
            Cambia a PRO solo cuando el software esté registrado en AEAT y hayas verificado el funcionamiento en PRE.<br/>
            El NIF emisor se toma del campo <b>CIF/NIF</b> en la pestaña Empresa.
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end' }}>
            <Btn variant="primary" onClick={save}>💾 Guardar configuración</Btn>
          </div>
        </Card>
      )}

      {/* Botones guardar / reset */}
      {tab !== 'portal' && tab !== 'email' && tab !== 'verifactu' && restoreMsg && (
        <div style={{ marginBottom:10, padding:'8px 12px', borderRadius:8, background: restoreMsg.startsWith('✓') ? '#d1fae5' : '#fee2e2', color: restoreMsg.startsWith('✓') ? '#065f46' : '#991b1b', fontSize:13, fontWeight:600 }}>
          {restoreMsg}
        </div>
      )}
      {tab !== 'portal' && tab !== 'email' && tab !== 'verifactu' && <div style={{ display:'flex', gap:10, alignItems:'center', justifyContent:'space-between', marginTop:4 }}>
        <div style={{ display:'flex', gap:8 }}>
          <label style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:8, border:`1px solid ${theme.borderStrong}`, background:theme.surface, fontSize:13, fontWeight:500, cursor:'pointer', color:theme.body }}>
            {restoring ? '⏳ Cargando...' : '📂 Cargar backup'}
            <input type="file" accept=".json" onChange={handleRestore} style={{ display:'none' }} />
          </label>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {saved && <span style={{ fontSize:13, color:'#0e9f6e', fontWeight:600 }}>✓ Guardado correctamente</span>}
          <Btn variant="primary" onClick={save}>💾 Guardar configuración</Btn>
        </div>
      </div>}

    </div>
  );
}
